import React, { useMemo } from 'react';
import { SelectField, useRecordContext } from 'react-admin';

import { get, isEmpty } from 'lodash';
import { Box, Grid, Typography } from '@mui/material';
import Answer from '../Answer';

import languages, { Languages } from '../../../../constants/languages';
import { countryChoices } from '../../../../constants/countries';
import conflictFields from '../../../../constants/conflictFields';
import applyChoices from '../../applyChoices';
import { TaxResidencyParameters } from '../../questionParameters';

const CONTENT = {
  [languages.SV]: 'Skatterättslig hemvist',
  [languages.EN]: 'Tax residency ',
  [languages.DE]: 'Steuerlicher Wohnsitz',
};

const ValueField = ({
  language,
  extraParameters,
}: {
  language?: Languages;
  extraParameters?: TaxResidencyParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value') as string[] | undefined;
  const sx = get(record, 'sx', {});

  if (!language) return null;

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" gap={0}>
        {value?.map((item, i) => (
          <React.Fragment key={item}>
            <SelectField
              sx={sx}
              record={{ item }}
              source="item"
              choices={applyChoices(
                countryChoices(language),
                extraParameters?.options,
                extraParameters?.choiceLabels,
              )}
              fontWeight="bold"
            />
            {i + 1 !== value.length && <Typography fontWeight="bold" mr={1}>,</Typography>}
          </React.Fragment>
        ))}
        {isEmpty(value) && <Typography>-</Typography>}
      </Box>
    </Box>
  );
};

const TaxResidency = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: TaxResidencyParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.label ?? CONTENT[language]}
      source={source ?? 'taxResidency'}
      datapoint={conflictFields.TAX_RESIDENCY}
      identificationInfo={undefined}
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

TaxResidency.ValueField = ValueField;

const Nested = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: TaxResidencyParameters;
}) => {
  const record = useRecordContext();
  const sx = get(record, 'sx', {});

  const language = record?.language as Languages;

  const taxResidency = useMemo(
    () => get(record, source ?? 'taxResidency', []),
    [source, record],
  ) as string[] | undefined;

  return (
    <Grid
      size={{
        xs: 12,
        md: 3,
      }}
    >
      <Box display="flex" flexDirection="column">
        <Typography variant="label">{extraParameters?.label ?? CONTENT[language]}</Typography>
        <Box display="flex" gap={0}>
          {taxResidency?.map((item, i) => (
            <React.Fragment key={item}>
              <SelectField
                record={{ item }}
                source="item"
                choices={applyChoices(
                  countryChoices(language),
                  extraParameters?.options,
                  extraParameters?.choiceLabels,
                )}
                sx={sx}
              />
              {i + 1 !== taxResidency.length && <Typography mr={1}>,</Typography>}
            </React.Fragment>
          ))}
          {isEmpty(taxResidency) && <Typography>-</Typography>}
        </Box>
      </Box>
    </Grid>
  );
};

TaxResidency.Nested = Nested;

export default TaxResidency;
